import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useAppSelector } from '@store/hooks'
import {
  getGoogleWorkspaceErrorMessage,
  GoogleWorkspaceStatus,
  isEmailBackendEnabled,
  requestConnectGoogleWorkspace,
  requestDisconnectGoogleWorkspace,
  requestGoogleAuthUrl,
  requestGoogleWorkspaceStatus,
  requestSyncGoogleWorkspaceInbox,
} from '@services/api/email.api'

export interface IGoogleWorkspaceHooks {
  status: GoogleWorkspaceStatus | null
  loading: boolean
  connecting: boolean
  syncing: boolean
  backendEnabled: boolean
  handleConnect: () => Promise<void>
  handleDisconnect: () => Promise<void>
  handleSync: () => Promise<void>
}

type UseGoogleWorkspaceOptions = {
  onMailboxConnected?: () => Promise<void>
  onMailboxDisconnected?: () => Promise<void>
}

const useGoogleWorkspace = (
  options: UseGoogleWorkspaceOptions = {},
): IGoogleWorkspaceHooks => {
  const { onMailboxConnected, onMailboxDisconnected } = options
  const { showToast } = useNotificationToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )

  const [status, setStatus] = useState<GoogleWorkspaceStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [syncing, setSyncing] = useState(false)

  // Guards against React StrictMode double-invoking the one-time code exchange.
  const exchangedCode = useRef<string | null>(null)

  const refreshStatus = useCallback(async (): Promise<void> => {
    if (!isEmailBackendEnabled()) return
    setLoading(true)
    try {
      setStatus(await requestGoogleWorkspaceStatus())
    } catch (error) {
      showToast(
        createFailedToast(
          'Google email',
          getGoogleWorkspaceErrorMessage(error),
        ),
      )
    } finally {
      setLoading(false)
    }
  }, [showToast])

  const handleConnect = useCallback(async (): Promise<void> => {
    setConnecting(true)
    try {
      const url = await requestGoogleAuthUrl()
      window.location.href = url
    } catch (error) {
      setConnecting(false)
      showToast(
        createFailedToast(
          'Google email',
          getGoogleWorkspaceErrorMessage(error),
        ),
      )
    }
  }, [showToast])

  const handleDisconnect = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      await requestDisconnectGoogleWorkspace()
      setStatus(null)
      await onMailboxDisconnected?.()
      showToast(createSuccessToast('Google email', 'Mailbox disconnected.'))
    } catch (error) {
      showToast(
        createFailedToast(
          'Google email',
          getGoogleWorkspaceErrorMessage(error),
        ),
      )
    } finally {
      setLoading(false)
    }
  }, [showToast, onMailboxDisconnected])

  const handleSync = useCallback(async (): Promise<void> => {
    setSyncing(true)
    try {
      const { synced } = await requestSyncGoogleWorkspaceInbox()
      showToast(
        createSuccessToast(
          'Google email',
          synced > 0
            ? `Synced ${synced} new message(s).`
            : 'Inbox is up to date.',
        ),
      )
    } catch (error) {
      showToast(
        createFailedToast(
          'Google email',
          getGoogleWorkspaceErrorMessage(error),
        ),
      )
    } finally {
      setSyncing(false)
    }
  }, [showToast])

  // Handle the OAuth redirect: Google returns `code` (+ `state`) on this page.
  useEffect(() => {
    const code = searchParams.get('code')
    if (!code || exchangedCode.current === code) {
      void refreshStatus()
      return
    }
    exchangedCode.current = code

    const params = new URLSearchParams(searchParams)
    params.delete('code')
    params.delete('state')
    params.delete('scope')
    params.delete('authuser')
    params.delete('prompt')
    setSearchParams(params, { replace: true })

    setConnecting(true)
    void (async (): Promise<void> => {
      try {
        const account = await requestConnectGoogleWorkspace(code)
        setStatus(account)
        await onMailboxConnected?.()
        showToast(
          createSuccessToast(
            'Google email',
            `Connected ${account.googleEmail}.`,
          ),
        )
      } catch (error) {
        showToast(
          createFailedToast(
            'Google email',
            getGoogleWorkspaceErrorMessage(error),
          ),
        )
      } finally {
        setConnecting(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload connection status when the navbar clinic changes.
  useEffect(() => {
    if (!selectedClinicId) {
      setStatus(null)
      return
    }
    void refreshStatus()
  }, [selectedClinicId, refreshStatus])

  return {
    status,
    loading,
    connecting,
    syncing,
    backendEnabled: isEmailBackendEnabled(),
    handleConnect,
    handleDisconnect,
    handleSync,
  }
}

export default useGoogleWorkspace
